import {
  WEEKDAYS,
  describeRecurrence,
  formatTimeOfDay,
  nextOccurrence,
  validateRecurrence,
  weekdayName,
  type Recurrence,
  type RecurrenceKind,
  type ReminderSchedule,
  type TimeOfDay,
} from '@anytracker/core'
import { useState } from 'react'

import { Button } from '../../components/Button.js'
import { Sheet } from '../../components/Sheet.js'
import { formatTime, formatUpcomingDay } from '../../format/dates.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Templates.module.css'

/**
 * The shape this section edits, kept in the parent so saving is one action.
 *
 * A `Recurrence` plus whether it is on. Off is a real state, not an absence: a
 * paused reminder keeps its schedule, so turning it back on does not mean
 * setting the times up again.
 */
export interface WhenToAskState {
  readonly enabled: boolean
  readonly recurrence: Recurrence
}

export const DEFAULT_WHEN: WhenToAskState = {
  enabled: false,
  recurrence: { kind: 'daily', times: [{ hour: 8, minute: 0 }] },
}

/**
 * Named by purpose, not by the union's tag.
 *
 * Nobody choosing a reminder is thinking in terms of `daily` / `weekly` /
 * `interval` — the same reasoning the CSV screen uses to name its two shapes by
 * what they are for rather than by their table structure.
 */
const KINDS: readonly { readonly kind: RecurrenceKind; readonly label: string }[] = [
  { kind: 'daily', label: 'Every day' },
  { kind: 'weekly', label: 'Certain days' },
  { kind: 'interval', label: 'Through the day' },
]

function withKind(state: WhenToAskState, kind: RecurrenceKind): WhenToAskState {
  const { recurrence } = state
  if (recurrence.kind === kind) {
    return state
  }
  /*
   * Times carry across a kind change where the new kind has them.
   *
   * Switching from "every day at 07:00" to "certain days" and finding the time
   * reset would be the app discarding something the user typed for no reason.
   * No lock is needed here, unlike a track's kind — a reminder has no history
   * to reinterpret.
   */
  const times = 'times' in recurrence ? recurrence.times : [{ hour: 8, minute: 0 }]
  switch (kind) {
    case 'daily':
      return { ...state, recurrence: { kind: 'daily', times } }
    case 'weekly':
      return {
        ...state,
        recurrence: { kind: 'weekly', weekdays: [1, 2, 3, 4, 5], times },
      }
    case 'interval':
      return {
        ...state,
        recurrence: {
          kind: 'interval',
          everyHours: 4,
          from: times[0] ?? { hour: 8, minute: 0 },
          to: { hour: 20, minute: 0 },
        },
      }
  }
}

interface Props {
  readonly value: WhenToAskState
  readonly onChange: (next: WhenToAskState) => void
}

/**
 * When a check-in should ask (T-916).
 *
 * A section of the check-in editor rather than a screen of its own, because it
 * is the same question one field along: the editor above says *what* a check-in
 * asks, and this says *when*. It also matches how somebody says it out loud —
 * "Morning: sleep, energy, mood, at 07:00 every day" — which is one object and
 * therefore one editor.
 *
 * **Nothing here implies a notification will arrive.** ADR-0004 puts delivery on
 * the native app, and a static site cannot send a push. What a schedule does on
 * web is decide when the check-in reads as due on Home (T-902), which is a real
 * job and is what the copy describes. A test greps this text for the language
 * of delivery.
 */
export function WhenToAsk({ value, onChange }: Props) {
  const { clock } = useServices()
  const [editingTime, setEditingTime] = useState<number | null>(null)

  const { enabled, recurrence } = value
  const times = 'times' in recurrence ? recurrence.times : []

  function setTimes(next: readonly TimeOfDay[]) {
    if (recurrence.kind === 'interval') {
      return
    }
    onChange({ ...value, recurrence: { ...recurrence, times: next } })
  }

  const check = validateRecurrence(recurrence)

  /*
   * The preview runs the **real** `nextOccurrence` against the unsaved
   * schedule. A second implementation here is how a preview ends up promising
   * something the stored schedule will not do — and this is the only way to
   * check a recurrence without waiting a day for it.
   */
  const now = clock.now()
  const preview: ReminderSchedule = {
    id: 'preview',
    name: 'preview',
    enabled: true,
    recurrence,
    createdAt: now,
    updatedAt: now,
  }
  const upcoming = check.ok ? nextOccurrence(preview, now, clock.timeZone()) : null

  return (
    <section className={styles['section']} data-bleed>
      <h2 className={styles['sectionTitle']}>When to ask</h2>

      <label className={styles['toggleRow']}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => {
            // Off keeps the schedule. Turning it back on must not mean setting
            // the times up again.
            onChange({ ...value, enabled: event.target.checked })
          }}
        />
        <span>Remind me to do this check-in</span>
      </label>

      <p className={styles['whenNote']}>
        A schedule decides when this check-in shows as due on your home screen.
        {/*
          Said plainly, and this is the sentence the delivery test reads. It
          states what happens rather than apologising for what does not.
        */}{' '}
        Nothing is sent to you — that arrives with the mobile app.
      </p>

      {enabled && (
        <>
          <div className={styles['kinds']} role="group" aria-label="How often">
            {KINDS.map((option) => (
              <button
                key={option.kind}
                type="button"
                aria-pressed={recurrence.kind === option.kind}
                className={`${styles['kindOption']} ${recurrence.kind === option.kind ? styles['kindSelected'] : ''}`}
                onClick={() => {
                  onChange(withKind(value, option.kind))
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          {recurrence.kind === 'weekly' && (
            <div className={styles['days']} role="group" aria-label="Days">
              {WEEKDAYS.map((day) => {
                const on = recurrence.weekdays.includes(day)
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    className={`${styles['day']} ${on ? styles['daySelected'] : ''}`}
                    onClick={() => {
                      onChange({
                        ...value,
                        recurrence: {
                          ...recurrence,
                          weekdays: on
                            ? recurrence.weekdays.filter((each) => each !== day)
                            : [...recurrence.weekdays, day].sort((a, b) => a - b),
                        },
                      })
                    }}
                  >
                    {weekdayName(day).slice(0, 3)}
                  </button>
                )
              })}
            </div>
          )}

          {recurrence.kind !== 'interval' && (
            <div className={styles['times']}>
              {times.map((time, index) => (
                <div
                  key={`${String(time.hour)}:${String(time.minute)}:${String(index)}`}
                  className={styles['timeRow']}
                >
                  <button
                    type="button"
                    className={styles['timeButton']}
                    onClick={() => {
                      setEditingTime(index)
                    }}
                  >
                    {formatTimeOfDay(time)}
                  </button>
                  {times.length > 1 && (
                    <button
                      type="button"
                      className={styles['timeRemove']}
                      aria-label={`Remove ${formatTimeOfDay(time)}`}
                      onClick={() => {
                        setTimes(times.filter((_, at) => at !== index))
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className={styles['addTime']}
                onClick={() => {
                  setTimes([...times, { hour: 20, minute: 0 }])
                }}
              >
                + Add a time
              </button>
            </div>
          )}

          {recurrence.kind === 'interval' && (
            <div className={styles['interval']}>
              <label className={styles['intervalField']}>
                <span>Every</span>
                <input
                  type="number"
                  min={1}
                  max={24}
                  aria-label="Hours between reminders"
                  value={recurrence.everyHours}
                  onChange={(event) => {
                    onChange({
                      ...value,
                      recurrence: {
                        ...recurrence,
                        everyHours: Number(event.target.value),
                      },
                    })
                  }}
                />
                <span>hours</span>
              </label>
              <label className={styles['intervalField']}>
                <span>From</span>
                <input
                  type="time"
                  aria-label="Start of day"
                  value={formatTimeOfDay(recurrence.from)}
                  onChange={(event) => {
                    const parsed = parseTime(event.target.value)
                    if (parsed !== null) {
                      onChange({ ...value, recurrence: { ...recurrence, from: parsed } })
                    }
                  }}
                />
              </label>
              <label className={styles['intervalField']}>
                <span>Until</span>
                <input
                  type="time"
                  aria-label="End of day"
                  value={formatTimeOfDay(recurrence.to)}
                  onChange={(event) => {
                    const parsed = parseTime(event.target.value)
                    if (parsed !== null) {
                      onChange({ ...value, recurrence: { ...recurrence, to: parsed } })
                    }
                  }}
                />
              </label>
            </div>
          )}

          {/*
            Both lines from core: the rule from `describeRecurrence`, the
            instant from `nextOccurrence`. This section does not phrase the rule
            itself, so what it promises is what gets stored.
          */}
          <div className={styles['preview']} data-testid="when-preview">
            <p className={styles['previewLine']}>{describeRecurrence(recurrence)}</p>
            <p className={styles['previewNext']}>
              {upcoming === null
                ? 'Not a valid schedule yet.'
                : `Next due ${formatUpcomingDay(upcoming, now)} at ${formatTime(upcoming)}.`}
            </p>
          </div>

          {/*
            Every failure at once, from core — a schedule that can never fire is
            refused with a reason rather than silently saved.
          */}
          {!check.ok && (
            <div className={styles['errors']} role="alert">
              {check.errors.map((error) => (
                <span key={error.code} className={styles['error']}>
                  {error.message}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      <Sheet
        open={editingTime !== null}
        title="Time"
        onClose={() => {
          setEditingTime(null)
        }}
      >
        <div className={styles['timeSheet']}>
          <label className={styles['intervalField']}>
            <span>At</span>
            <input
              type="time"
              aria-label="Time of day"
              value={formatTimeOfDay(times[editingTime ?? 0] ?? { hour: 8, minute: 0 })}
              onChange={(event) => {
                const parsed = parseTime(event.target.value)
                if (parsed !== null && editingTime !== null) {
                  setTimes(times.map((time, at) => (at === editingTime ? parsed : time)))
                }
              }}
            />
          </label>
          <Button
            onClick={() => {
              setEditingTime(null)
            }}
          >
            Done
          </Button>
        </div>
      </Sheet>
    </section>
  )
}

/**
 * `HH:MM` to a wall clock.
 *
 * A wall clock, never an instant — 08:00 is 08:00 through a DST change and
 * after a flight, which is the opposite of how entries are stored and for the
 * reason set out beside the reminder model.
 */
function parseTime(value: string): TimeOfDay | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (!match) {
    return null
  }
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour > 23 || minute > 59) {
    return null
  }
  return { hour, minute }
}
